import { useEffect, useState } from 'react';
import { VideoJsPlayer } from 'video.js';
import { AbsoluteCenter } from '../styles';
import { PLAY_STATE_EVENTS } from '../PlayerServices';
import styled from '@emotion/styled';
import { IconPause, IconPlayBig } from '../../../icons';

const PlayControls = ({ player, showPlayBtn }: { player: VideoJsPlayer; showPlayBtn: boolean }) => {
  const [playState, setPlayState] = useState<'play' | 'pause' | 'ended'>(player && player?.paused() ? 'pause' : 'play');

  useEffect(() => {
    // Read paused() and listen to every name videojs-contrib-ads can give these events: while an
    // ad plays they reach the player as 'adplaying' and 'adpause', so a listener bound to the
    // plain names never hears about the ad being paused and the icon stays on its last state.
    const handlePlayStateChange = () => setPlayState(player.paused() ? 'pause' : 'play');

    player.on(PLAY_STATE_EVENTS, handlePlayStateChange);
    return () => {
      // By reference: player.off('pause') without one would drop the pause listener of every
      // other component and the redispatching of contrib-ads along with this one.
      player.off(PLAY_STATE_EVENTS, handlePlayStateChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayVideo = () => {
    // Toggle what the player is actually doing rather than what the icon shows.
    if (player.paused()) {
      setPlayState('play');
      player.play();
    } else {
      setPlayState('pause');
      player.pause();
    }
  };

  return (
    <BigBtnFloatingContainer show={showPlayBtn}>
      <BigBtnFloatingButton isPlay={true} onClick={() => handlePlayVideo()}>
        <AbsoluteCenter>{getPlayStateIcon(playState)}</AbsoluteCenter>
      </BigBtnFloatingButton>
    </BigBtnFloatingContainer>
  );
};

export default PlayControls;

export const getPlayStateIcon = (playState) => {
  if (playState === 'pause') {
    return <IconPlayBig />;
  } else if (playState === 'play') {
    return <IconPause />;
  }
  return <IconPlayBig />;
};

export const BigBtnFloatingContainer = styled(AbsoluteCenter)<{ show: boolean }>(({ show }) => ({
  display: show ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'opacity .3s ease',
  direction: 'rtl',
  zIndex: 2,
}));

export const BigBtnFloatingButton = styled.button<{ isPlay: boolean }>(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.rgba(theme.palette.background[800][0], 0.4),
  cursor: 'pointer',
  minWidth: '6.7vw',
  height: '6.7vw',
  margin: '0 3vw',
  borderRadius: '50%',
  borderStyle: 'solid',
  borderColor: 'transparent',
  ':hover': {
    backgroundColor: theme.rgba(theme.palette.background[800][0], 0.6),
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  '& svg': {
    width: '3vw',
    height: '3vw',
  },
  '@media (max-width: 1000px) and (min-width: 701px)': {
    minWidth: '10vw',
    height: '10vw',
    '& svg': {
      width: '5vw',
      height: '5vw',
    },
  },
  '@media (max-width: 700px)': {
    minWidth: '15vw',
    height: '15vw',
    '& svg': {
      width: '8vw',
      height: '8vw',
    },
  },
}));
